import { useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck, faCopy, faEnvelope } from '@fortawesome/free-solid-svg-icons'

interface InviteSuccessCardProps {
  email: string
  registrationUrl: string
  expiresAt: string
  onInviteAnother?: () => void
  onViewCustomer?: () => void
}

function formatExpiry(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export default function InviteSuccessCard({
  email,
  registrationUrl,
  expiresAt,
  onInviteAnother,
  onViewCustomer,
}: InviteSuccessCardProps) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(registrationUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy invite link', err)
    }
  }

  return (
    <div className="border border-green-200 bg-green-50 rounded-lg p-6 space-y-4">
      <div className="flex items-start gap-3">
        <FontAwesomeIcon icon={faEnvelope} className="text-green-600 mt-1" />
        <div>
          <h3 className="text-lg font-semibold text-green-900">Invitation sent</h3>
          <p className="text-sm text-green-800">
            An email was sent to <span className="font-medium">{email}</span> with a registration link.
          </p>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-600 mb-1">
          Registration link (valid until {formatExpiry(expiresAt)})
        </label>
        <div className="flex gap-2">
          <input
            type="text"
            readOnly
            value={registrationUrl}
            onFocus={(e) => e.currentTarget.select()}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
          />
          <button
            type="button"
            onClick={handleCopy}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 inline-flex items-center gap-2"
          >
            <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>

      <div className="flex gap-2 pt-2">
        {onInviteAnother && (
          <button
            type="button"
            onClick={onInviteAnother}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50"
          >
            Invite another
          </button>
        )}
        {onViewCustomer && (
          <button
            type="button"
            onClick={onViewCustomer}
            className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            View customer
          </button>
        )}
      </div>
    </div>
  )
}
